import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";

const Card = ({
  title,
  subtitle,
  codice,
  ruolo,
  active,
  onSelect,
  disabled,
  badge,
  servizioSpeciale = false,
}) => {
  return (
    <div
      className={`rounded-2xl border p-5 shadow-sm transition transform relative overflow-hidden ${
        active
          ? "border-primary-500 bg-primary-50"
          : "border-gray-200 bg-white hover:shadow-md"
      }`}
    >
      <span className="accent-bar top-5 bottom-5" aria-hidden="true" />
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">
            {badge}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            {servizioSpeciale && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-800">
                Servizio Speciale
              </span>
            )}
          </div>
          {codice && (
            <p className="text-sm text-gray-500">Codice: {codice}</p>
          )}
        </div>
        <div
          className={`h-10 w-10 rounded-full flex items-center justify-center ${
            active ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-600"
          }`}
        >
          <ArrowsRightLeftIcon className="h-5 w-5" />
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
          Ruolo: {ruolo || "volontario"}
        </span>
        <button
          onClick={onSelect}
          disabled={disabled}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
            active
              ? "bg-primary-600 text-white cursor-default"
              : "bg-white text-primary-600 border border-primary-200 hover:bg-primary-50 disabled:opacity-50"
          }`}
        >
          {active ? "Servizio attivo" : "Opero qui"}
        </button>
      </div>
    </div>
  );
};

const ScegliServizio = () => {
  const {
    user,
    activeCongregazione,
    setActiveCongregazione,
    delegheInfo,
    delegheLoading,
    refreshDeleghe,
  } = useAuth();
  const [switchingId, setSwitchingId] = useState(null);

  const availableOptions = useMemo(() => {
    const list = [];
    const primaria =
      delegheInfo?.primaria ||
      (user?.congregazione_id
        ? {
            id: user.congregazione_id,
            nome: user.congregazione_nome,
            codice: user.congregazione_codice,
            ruolo: user?.ruolo,
            servizio_speciale: user?.congregazione_servizio_speciale ?? false,
            rapporto_servizio_attivo:
              user?.congregazione_rapporto_servizio_attivo ?? false,
            gruppi_servizio_visibili:
              user?.congregazione_gruppi_servizio_visibili ?? false,
          }
        : null);

    if (primaria?.id) {
      list.push({
        id: primaria.id,
        nome: primaria.nome,
        codice: primaria.codice,
        tipo: "Principale",
            ruolo: primaria.ruolo || user?.ruolo_base || user?.ruolo,
        servizio_speciale: Boolean(primaria.servizio_speciale),
        rapporto_servizio_attivo:
          primaria.rapporto_servizio_attivo ??
          primaria.congregazione_rapporto_servizio_attivo,
        gruppi_servizio_visibili:
          primaria.gruppi_servizio_visibili ??
          primaria.congregazione_gruppi_servizio_visibili,
      });
    }

    (delegheInfo?.deleghe || []).forEach((delega) => {
      if (!list.some((item) => item.id === delega.congregazione_id)) {
        list.push({
          id: delega.congregazione_id,
          nome: delega.congregazione_nome,
          codice: delega.congregazione_codice,
          tipo: "Delega",
          ruolo: delega.ruolo_delega,
          servizio_speciale: Boolean(
            delega.congregazione_servizio_speciale ??
              delega.servizio_speciale
          ),
          rapporto_servizio_attivo:
            delega.congregazione_rapporto_servizio_attivo ??
            delega.rapporto_servizio_attivo,
          gruppi_servizio_visibili:
            delega.congregazione_gruppi_servizio_visibili ??
            delega.gruppi_servizio_visibili,
        });
      }
    });

    return list;
  }, [delegheInfo, user]);

  const handleSelect = async (option) => {
    if (activeCongregazione?.id === option.id) {
      return;
    }

    if (activeCongregazione?.id && activeCongregazione.id !== option.id) {
      const confirmed = window.confirm(
        `Stai per cambiare congregazione attiva in "${option.nome}". Vuoi continuare?`
      );
      if (!confirmed) {
        return;
      }
    }

    setSwitchingId(option.id);
    try {
      setActiveCongregazione({
        id: option.id,
        codice: option.codice,
        nome: option.nome,
        servizio_speciale: option.servizio_speciale,
        rapporto_servizio_attivo: option.rapporto_servizio_attivo,
        gruppi_servizio_visibili: option.gruppi_servizio_visibili,
      });
      toast.success(`Ora stai operando per ${option.nome}`);
      if (option.servizio_speciale) {
        toast("Modalità Servizio Speciale attiva. Usa i delegati dedicati.", {
          icon: (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-white">
              <CheckIcon className="h-4 w-4" />
            </span>
          ),
        });
      }
    } finally {
      setSwitchingId(null);
    }
  };

  const showSuperAdminHint =
    user?.ruolo === "super_admin" && availableOptions.length === 0;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-wide text-primary-600 font-semibold">
                Scegli Servizio
              </p>
              <h1 className="text-2xl font-bold text-gray-900 mt-1">
                Seleziona la congregazione su cui vuoi operare
              </h1>
              <p className="text-gray-600 mt-2">
                Puoi passare rapidamente dal servizio principale agli incarichi
                delegati. Ogni azione che esegui riguarderà la congregazione
                attiva mostrata nella barra superiore.
              </p>
              <div className="accent-line mt-4" />
            </div>
            <button
              onClick={refreshDeleghe}
              disabled={delegheLoading}
              className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Aggiorna elenco
            </button>
          </div>
        </div>

        {activeCongregazione?.servizio_speciale && (
          <div className="bg-purple-50/80 border border-purple-200 rounded-2xl p-4 flex items-start gap-3">
            <CheckCircleIcon className="h-5 w-5 text-purple-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-purple-800">
                Modalità Servizio Speciale attiva
              </p>
              <p className="text-sm text-purple-700">
                Ricorda che per questa congregazione l'autocompilazione utilizza esclusivamente i delegati e le loro disponibilità dedicate.
              </p>
            </div>
          </div>
        )}

        {delegheLoading ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Caricamento deleghe...</p>
            </div>
          </div>
        ) : showSuperAdminHint ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <p className="text-gray-600">
              Sei un Super Admin. Usa la sezione{" "}
              <strong>Congregazioni</strong> per selezionare su quale realtà
              operare.
            </p>
          </div>
        ) : availableOptions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <p className="text-gray-600">
              Nessun servizio disponibile al momento. Contatta il tuo
              amministratore per ottenere una delega.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {availableOptions.map((option) => (
              <Card
                key={option.id}
                title={option.nome}
                codice={option.codice}
                subtitle={
                  option.tipo === "Principale"
                    ? "Congregazione principale assegnata"
                    : "Delegato da admin di questa congregazione"
                }
              ruolo={option.ruolo}
              badge={option.tipo}
              servizioSpeciale={option.servizio_speciale}
                active={activeCongregazione?.id === option.id}
                disabled={switchingId === option.id}
                onSelect={() => handleSelect(option)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScegliServizio;
